import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { User, UserStatus } from "~/api/model";

import { getListUsersQueryKey, useDeleteUser } from "~/api/users/users";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useSettingsParams } from "~/helpers";

interface DeleteUserDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
}

enum DeleteDialogStep {
  DeleteUser = 1,
  Success = 2,
  Error = 3,
}

export const DeleteUserDialog: FC<DeleteUserDialogProps> = ({
  open,
  setOpen,
  user,
}) => {
  const [step, setStep] = useState<DeleteDialogStep>(
    DeleteDialogStep.DeleteUser
  );
  const { organizationId } = useSettingsParams();
  const queryClient = useQueryClient();

  const { mutate: deleteUser, isPending } = useDeleteUser();

  const onSubmit = () => {
    deleteUser(
      { organizationId, userId: user.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListUsersQueryKey(organizationId),
          });
          if (user.status === UserStatus.FORCE_CHANGE_PASSWORD) {
            setOpen(false);
          } else {
            setStep(DeleteDialogStep.Success);
          }
        },
        onError: () => {
          setStep(DeleteDialogStep.Error);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {step === DeleteDialogStep.DeleteUser &&
              (user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                <FormattedMessage
                  id="settings.team.invitationCancel"
                  defaultMessage="Cancel Invite"
                />
              ) : (
                <FormattedMessage
                  id="settings.team.deleteMember"
                  defaultMessage="Delete Team Member"
                />
              ))}
            {step === DeleteDialogStep.Success && (
              <FormattedMessage id="success" defaultMessage="Success" />
            )}
            {step === DeleteDialogStep.Error && (
              <FormattedMessage id="error" defaultMessage="Error" />
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {step === DeleteDialogStep.DeleteUser &&
              (user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                <FormattedMessage
                  id="settings.team.invitationCancel.areYouSure"
                  values={{
                    userEmail: user.email,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="settings.team.deleteMember.confirmation"
                  values={{
                    b: (chunks) => <strong>{chunks}</strong>,
                    userName: user.name,
                    userEmail: user.email,
                  }}
                />
              ))}
            {step === DeleteDialogStep.Success && (
              <FormattedMessage
                id="settings.team.delete.success"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  userName: user.name,
                  userEmail: user.email,
                }}
              />
            )}
            {step === DeleteDialogStep.Error && (
              <FormattedMessage
                id="settings.team.invite.error"
                defaultMessage="Something went wrong. Please try again later."
              />
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {step === DeleteDialogStep.DeleteUser && (
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                <FormattedMessage id="close" defaultMessage="Close" />
              ) : (
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              )}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={isPending}
              variant="destructive"
            >
              {user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              ) : (
                <FormattedMessage id="delete" defaultMessage="Delete" />
              )}
            </Button>
          </div>
        )}
        {step !== DeleteDialogStep.DeleteUser && (
          <Button
            variant="outline"
            className="ml-auto"
            onClick={() => setOpen(false)}
          >
            <FormattedMessage id="close" defaultMessage="Close" />
          </Button>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
